/**
 * BulkResultsDialog — what a bulk action actually did, when not all of it
 * went through. The all-good case is a toast from the caller; this dialog
 * exists for the other case, where the person needs to read which items were
 * refused and why before moving on. The failed items stay selected in the
 * list behind it.
 */
import { Modal, Callout } from '@/components/ds';
import { Button } from '@/components/ui/Button';
import { itemsWord, type BulkActionResult } from './index';

interface BulkResultsDialogProps {
  result: BulkActionResult | null;
  onClose: () => void;
}

export function BulkResultsDialog({ result, onClose }: BulkResultsDialogProps) {
  const open = result !== null;
  const failedCount = result?.failed.length ?? 0;
  const allFailed = !!result && result.succeeded === 0;

  // Ten identical refusals read as one line.
  const byReason = new Map<string, string[]>();
  for (const f of result?.failed ?? []) {
    const names = byReason.get(f.reason) ?? [];
    names.push(f.itemTitle.trim() || 'Untitled item');
    byReason.set(f.reason, names);
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="md"
      title={
        result
          ? allFailed
            ? `${result.action}: none of the ${itemsWord(result.total)}`
            : `${result.action}: ${result.succeeded} of ${result.total}`
          : ''
      }
      description={
        result
          ? `${itemsWord(failedCount)} could not be changed. ${
              allFailed ? 'They stay' : 'The ones that failed stay'
            } selected so you can open them or try again.`
          : undefined
      }
      footer={
        <Button type="button" variant="cta" size="sm" onClick={onClose}>
          Close
        </Button>
      }
    >
      <div className="space-y-3">
        {Array.from(byReason.entries()).map(([reason, names]) => (
          <Callout key={reason} tone="coral" title={reason}>
            <ul className="mt-1 space-y-0.5">
              {names.map((name, i) => (
                <li key={`${name}-${i}`} className="truncate">
                  {name}
                </li>
              ))}
            </ul>
          </Callout>
        ))}
      </div>
    </Modal>
  );
}
